import {
  ForwardedRef,
  forwardRef,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Button, DatePicker, Flex, Space, Switch, Tooltip, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CopyOutlined, EditOutlined, EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import { NoUndefinedRangeValueType, RangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';
import { Guard, useGuard } from '@ohpen/react-authz';
import { Table, TableComponent, TableProps, CrudMode, TableColumns, InfoTooltip, getAllTableColumns, BoxedSwitch } from '@ohpen/enso-ui';

import { ActionEntityType } from '@pmp/api';
import { useCrudClone, useEntityExternalFilters, useListTableState, usePmpFetch, useTableView } from '@pmp/hooks';
import { cloneTableFields, type CloneRow, type CloneType, type ResolvedCloneRows } from '@pmp/assistant';
import { CrudTable, ProductCrudData } from '@pmp/components';
import { ChangeRequestsStateCrudTable } from '@pmp/pages';
import { ProductData } from '@pmp/types';
import { ChangeLogDrawer, ChangeLogTarget } from '../../changeLog';
import { useExportTemplatesStore } from '@pmp/stores';
import { formatDate } from '@pmp/utils';

import { productsColumns } from '../tableConfig';
import { ProductsListProps, ProductsListComponent, ViewState, ProductsListFilters } from '../interfaces';
import {
  filterActiveToday,
  flattenProductAdditionalFields,
  productsRequestYears,
  productsToProductsCrud,
  productsFiltersUrlCodec,
  productsYearRange,
  hasLockedProducts,
  removeReplacesCode,
} from '../utils';
import { ProductView } from './ProductView';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const entityType = ActionEntityType.PRODUCTS;
const rowKey = 'code';

// What the assistant may carry into a clone, read off the same column config the clone table is built from.
const cloneFields = cloneTableFields(productsColumns);

const getDefaultFilters = (): ProductsListFilters => ({
  dates: [dayjs().format('YYYY'), dayjs().add(1, 'year').format('YYYY')],
  activeToday: true,
});

const ProductsListBase = (
  { valueLists, additionalFields, fieldsLoading }: ProductsListProps,
  externalRef: ForwardedRef<ProductsListComponent>,
): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [viewData, setViewData] = useState<ViewState>({
    isOpen: false,
    record: null,
  });
  const [crudData, setCrudData] = useState<ChangeRequestsStateCrudTable>({
    isOpen: false,
    isLoading: false,
    data: null,
    mode: null,
    entityType,
  });
  const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
  const { tableViews, saveTableView, deleteTableView } = useTableView(ActionEntityType.PRODUCTS);
  const [productsLoading, , productsResult, , getProducts] = usePmpFetch('productsGetAllProducts');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const defaultFilters = useMemo(getDefaultFilters, []);
  const [{ dates, activeToday }, setFilters] = useEntityExternalFilters<ProductsListFilters>(
    entityType,
    defaultFilters,
    productsFiltersUrlCodec,
  );
  const [selectedRows, setSelectedRows] = useState<ProductData[]>([]);
  const [changeLogTarget, setChangeLogTarget] = useState<ChangeLogTarget | null>(null);
  const { allowed: showRowSelection } = useGuard({
    permissions: ['products:update'],
    flags: ['enableProductsCrud'],
  });
  const { t } = useTranslation();

  const dateRange = useMemo(() => productsYearRange(dates) ?? defaultFilters.dates, [dates, defaultFilters.dates]);

  const refreshTable = useCallback(() => {
    getProducts({ query: productsRequestYears(dateRange) });
  }, [dateRange, getProducts]);

  useImperativeHandle(externalRef, () => {
    return { refreshTable };
  });

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  useEffect(() => {
    tableRef.current?.resetFilteredData?.();
  }, [activeToday]);

  const columns: TableColumns<ProductData> = useMemo(() => {
    return getAllTableColumns(productsColumns, additionalFields);
  }, [additionalFields]);

  const tableState = useListTableState<ProductData>(entityType, rowKey, tableRef, columns);

  const resetSelectedRows = useCallback(() => {
    tableRef.current?.resetSelectedRows();
  }, []);

  const onView = useCallback((record: ProductData) => {
    setActiveRowId(record[rowKey] ?? null);
    setViewData({ isOpen: true, record });
  }, []);

  const onCreate = useCallback(() => {
    setCrudData({ isOpen: true, data: null, mode: CrudMode.CREATE, entityType });
  }, []);

  const onEdit = useCallback((record: ProductData) => {
    const data: ProductCrudData[] = productsToProductsCrud([record]);

    setActiveRowId(record[rowKey] ?? null);
    setCrudData({ isOpen: true, data, mode: CrudMode.EDIT, entityType });
  }, []);

  const onClone = useCallback((record: ProductData) => {
    const data: ProductCrudData[] = removeReplacesCode(productsToProductsCrud([record]));

    setActiveRowId(record[rowKey] ?? null);
    setCrudData({ isOpen: true, data, mode: CrudMode.CLONE, entityType, hasLockedOriginals: hasLockedProducts([record]) });
  }, []);

  const onBulkEdit = useCallback((selectedRows: ProductData[]) => {
    const data: ProductCrudData[] = productsToProductsCrud(selectedRows);

    setCrudData({ isOpen: true, data, mode: CrudMode.BULK_EDIT, entityType });
  }, []);

  const onBulkClone = useCallback((selectedRows: ProductData[]) => {
    const data: ProductCrudData[] = removeReplacesCode(productsToProductsCrud(selectedRows));

    setCrudData({ isOpen: true, data, mode: CrudMode.BULK_CLONE, entityType, hasLockedOriginals: hasLockedProducts(selectedRows) });
  }, []);

  // The clone the assistant proposes, opened exactly as the Clone buttons above open theirs. The rows it
  // hands over are the grid's own, with its changes already on them; the crud rows are built from the
  // originals so each copy keeps the code it came from, and the changes are laid over that. Passing the
  // originals as well is what makes the proposed cells read as changed in the table the user then reads.
  // The merged rows go through removeReplacesCode last: they carry the grid row's own fields, and a copy
  // that keeps the source's lock reads as caught in the source's pending change request.
  const onCloneProposed = useCallback((rows: CloneRow[], originals: CloneRow[], cloneType: CloneType) => {
    const originalData: ProductCrudData[] = removeReplacesCode(productsToProductsCrud(originals as ProductData[]));
    const data: ProductCrudData[] = removeReplacesCode(
      originalData.map((row, index) => ({ ...row, ...rows[index] }) as ProductCrudData),
    );

    setActiveRowId(rows.length === 1 ? ((rows[0][rowKey] as string) ?? null) : null);
    setCrudData({
      isOpen: true,
      data,
      originalData,
      cloneType,
      mode: rows.length === 1 ? CrudMode.CLONE : CrudMode.BULK_CLONE,
      entityType,
      hasLockedOriginals: hasLockedProducts(originals as ProductData[]),
    });
  }, []);

  const onCloseCrud = useCallback(() => {
    setCrudData({ isOpen: false, data: null, mode: null, entityType });
  }, []);

  const onCloseView = useCallback(() => {
    setViewData({ isOpen: false, record: null });
  }, []);

  const onChangeDate = useCallback(
    (_values: NoUndefinedRangeValueType<Dayjs> | null, dateStrings: [string, string]) => {
      setFilters(prev => ({ ...prev, dates: productsYearRange(dateStrings) ?? defaultFilters.dates }));
    },
    [defaultFilters.dates, setFilters],
  );

  const onChangeActiveToday = useCallback(
    (checked: boolean) => {
      setFilters(prev => ({ ...prev, activeToday: checked }));
    },
    [setFilters],
  );

  // Bulk edit/clone need the rows themselves; the assistant only ever gets their ids, and gets
  // nothing at all when the assistant flag is off (the hook returns no callback then).
  const onChangeSelectedRows = useCallback(
    (selectedRows: ProductData[]) => {
      setSelectedRows(selectedRows);
      tableState.onChangeSelectedRows?.(selectedRows);
    },
    [tableState],
  );

  const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.PRODUCTS), [getExportTemplates]);

  const getRowActions: (record: ProductData) => ReactElement[] = useCallback(
    record => {
      const editTitle = record.lockedByActionId ? (
        <>
          {t('global.buttons.editLockedByChangeRequest')}
          <Link to={`/change-requests?search-change-requests=${encodeURIComponent(record.lockedByActionId)}`}>
            {record.lockedByActionId}
          </Link>
        </>
      ) : (
        t('enso.buttons.edit')
      );
      const rowActions = [
        <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
          <Button type="text" aria-label="View Product" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['products:update']} flags={['enableProductsCrud']}>
          <Tooltip key="edit" title={editTitle} placement="top">
            <Button
              type="text"
              aria-label="Edit Product"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
          <Tooltip key="clone" title={t('global.buttons.clone')} placement="top">
            <Button type="text" aria-label="Clone Product" icon={<CopyOutlined />} onClick={() => onClone(record)} />
          </Tooltip>
        </Guard>,
        <Guard key="changeLog" flags={['enableChangeLog']}>
          <Tooltip title={t('lender.changeLog.labels.viewChangeLog')} placement="top">
            <Button
              type="text"
              aria-label="View change log"
              icon={<HistoryOutlined />}
              onClick={() =>
                setChangeLogTarget({ entityType: ActionEntityType.PRODUCTS, id: record.code ?? '', name: record.name ?? record.code ?? '' })
              }
            />
          </Tooltip>
        </Guard>,
      ];
      return rowActions;
    },
    [onClone, onEdit, onView, t],
  );

  const disabledBulkEdit: boolean = useMemo(() => selectedRows?.some(record => !!record.lockedByActionId) ?? false, [selectedRows]);

  const isLoading = useMemo(() => fieldsLoading || productsLoading, [fieldsLoading, productsLoading]);

  const flattenProducts: ProductData[] = useMemo(
    () => (productsResult ? flattenProductAdditionalFields(productsResult) : []),
    [productsResult],
  );

  const dataSource: ProductData[] = useMemo(
    () => (activeToday ? filterActiveToday(flattenProducts) : flattenProducts),
    [activeToday, flattenProducts],
  );

  // The rows a proposal names, looked up in the grid's own data so a code the list does not show cannot be
  // cloned. Naming none means the ones the user has selected, which is the gesture the proposal follows.
  const resolveCloneRows = useCallback(
    (keys: readonly string[]): ResolvedCloneRows => {
      if (keys.length === 0) return { rows: selectedRows as CloneRow[], missing: [] };

      // Matched without case: a product code is unique without it, and a code reaches here in whatever
      // case it was written in rather than the one the row carries.
      const byCode = new Map(dataSource.map(row => [String(row[rowKey]).toLowerCase(), row]));
      const found = keys.map(key => byCode.get(key.toLowerCase()));

      return {
        rows: found.filter((row): row is ProductData => row !== undefined) as CloneRow[],
        missing: keys.filter((_, index) => found[index] === undefined),
      };
    },
    [dataSource, selectedRows],
  );

  // Registered only for a user the portal would let clone a product anyway: the same permission and flag the
  // Clone buttons are guarded by.
  useCrudClone(showRowSelection ? entityType : undefined, {
    fields: cloneFields,
    resolveRows: resolveCloneRows,
    openWith: onCloneProposed,
  });

  const tableProps: TableProps<ActionEntityType, ProductData> = useMemo(
    () => ({
      ...tableState,
      id: entityType,
      itemsLabel: t('pmp.products.labels.products'),
      ref: tableRef,
      dataSource,
      columns,
      rowKey,
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
      resizableColumns: true,
      valueLists,
      loading: isLoading,
      activeRowId,
      initialGlobalSearchColumn: rowKey,
      getRowActions,
      showRowSelection,
      showViews: true,
      tableViews,
      saveTableView,
      deleteTableView,
      onChangeSelectedRows,
      additionalFields: true,
    }),
    [
      tableState,
      t,
      dataSource,
      columns,
      valueLists,
      isLoading,
      activeRowId,
      getRowActions,
      showRowSelection,
      tableViews,
      saveTableView,
      deleteTableView,
      onChangeSelectedRows,
    ],
  );

  const datePickerValues: RangeValueType<Dayjs> = useMemo(() => [dayjs(dateRange[0]), dayjs(dateRange[1])], [dateRange]);

  return (
    <>
      <Table {...tableProps}>
        <>
          <Table.ActionsBar>
            <Flex justify="space-between" gap="middle">
              <Space size="middle">
                <Table.GlobalSearch />
                <RangePicker value={datePickerValues} onChange={onChangeDate} picker="year" format="YYYY" disabled={productsLoading} />
                <InfoTooltip text={t('global.labels.infoTooltipText')} />
                <BoxedSwitch>
                  <Text>{t('pmp.products.labels.activeToday')}</Text>
                  <Switch value={activeToday} onChange={onChangeActiveToday} />
                </BoxedSwitch>
              </Space>
              <Space size="middle">
                <Table.ResetFiltersButton />
                <Table.SelectColumnsButton />
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    fileName={`Products available at ${dateRange} on ${formatDate()}`}
                    sheetName="Products"
                    disabled={isLoading}
                    loadingTemplates={isLoadingExportTemplates}
                    allowSearch={true}
                    getTemplates={getTemplates}
                    modalDefaults={{ columnScope: 'template' }}
                    editableFileName
                  />
                </Guard>
                <Guard permissions={['products:create']} flags={['enableProductsCrud']}>
                  <Table.CreateButton onClick={onCreate} />
                </Guard>
              </Space>
            </Flex>
          </Table.ActionsBar>
          <Table.BulkBar xItemsSelectedLabel="pmp.products.labels.xProductsSelected">
            <Space size="middle">
              <Table.BulkCloneButton onClick={onBulkClone} />
              <Table.BulkEditButton
                onClick={onBulkEdit}
                disabled={disabledBulkEdit}
                tooltip={disabledBulkEdit ? t('global.labels.bulkEditLockedByChangeRequest') : undefined}
              />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  fileName={`Products available at ${dateRange} on ${formatDate()}`}
                  sheetName="Products"
                  disabled={isLoading}
                  loadingTemplates={isLoadingExportTemplates}
                  allowSearch={true}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  editableFileName
                  exportView
                />
              </Guard>
            </Space>
          </Table.BulkBar>
        </>
      </Table>
      {viewData.isOpen && (
        <ProductView record={viewData.record} valueLists={valueLists} additionalFields={additionalFields} onClose={onCloseView} />
      )}
      <ChangeLogDrawer target={changeLogTarget} onClose={() => setChangeLogTarget(null)} />
      {crudData.isOpen && (
        <CrudTable
          entityType={ActionEntityType.PRODUCTS}
          data={crudData.data}
          originalData={crudData.originalData}
          columns={productsColumns}
          mode={crudData.mode}
          cloneType={crudData.cloneType}
          additionalFields={additionalFields}
          valueLists={valueLists}
          hasLockedOriginals={crudData.hasLockedOriginals}
          setActiveRowId={setActiveRowId}
          refreshTable={refreshTable}
          resetSelectedRows={resetSelectedRows}
          onClose={onCloseCrud}
        />
      )}
    </>
  );
};

export const ProductsList = memo(forwardRef(ProductsListBase));
